/**
 * JWT Token Management Utility
 *
 * JWT 토큰의 만료 체크, 자동 갱신, 저장/조회를 담당하는 유틸리티 모듈
 *
 * Features:
 * - Access Token 만료 체크 (15분)
 * - Refresh Token을 이용한 자동 갱신 (30일)
 * - 토큰 저장/조회 (localStorage)
 * - 토큰 만료시 자동 리프레시
 */

export interface TokenResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
}

export interface TokenInfo {
  isValid: boolean;
  isExpired: boolean;
  expiresAt: Date | null;
  timeUntilExpiry: number; // milliseconds
}

/**
 * JWT 토큰 관리 클래스 (쿠키 기반 Refresh Token)
 */
export class TokenManager {
  // Access Token은 메모리와 localStorage에서 관리 (페이지 새로고침 대응)
  private static accessToken: string | null = null;
  private static tokenExpiry: Date | null = null;
  
  // localStorage 키
  private static readonly ACCESS_TOKEN_KEY = 'goodfarmer_access_token';
  private static readonly TOKEN_EXPIRY_KEY = 'goodfarmer_token_expiry';

  // 토큰 만료 시간 (백엔드와 동일하게 설정)
  private static readonly ACCESS_TOKEN_EXPIRE_MINUTES = 15;
  private static readonly REFRESH_TOKEN_EXPIRE_DAYS = 30;

  // 토큰 갱신을 위한 버퍼 시간 (만료 5분 전에 갱신)
  private static readonly REFRESH_BUFFER_MINUTES = 5;

  // 로그아웃 진행 중 플래그
  private static isLoggingOut: boolean = false;

  // 세션 만료 콜백
  private static onSessionExpired?: (reason: 'no-token' | 'token-expired' | 'refresh-failed') => void;

  // 초기화 플래그
  private static initialized: boolean = false;

  /**
   * TokenManager 초기화 (localStorage에서 토큰 로드)
   */
  static initialize(): void {
    if (this.initialized) {
      return;
    }

    try {
      const storedToken = localStorage.getItem(this.ACCESS_TOKEN_KEY);
      const storedExpiry = localStorage.getItem(this.TOKEN_EXPIRY_KEY);

      if (storedToken && storedExpiry) {
        this.accessToken = storedToken;
        this.tokenExpiry = new Date(storedExpiry);
        
        // 토큰이 만료되었는지 확인
        const tokenInfo = this.getTokenInfo();
        if (!tokenInfo.isValid) {
          console.log('🔄 Stored token expired, clearing...');
          this.clearTokens();
        } else {
          console.log('🔄 Token loaded from localStorage on initialization');
        }
      }
    } catch (error) {
      console.warn('TokenManager 초기화 실패:', error);
    }

    this.initialized = true;
  }

  /**
   * 세션 만료 콜백 설정
   */
  static setSessionExpiredCallback(callback: (reason: 'no-token' | 'token-expired' | 'refresh-failed') => void): void {
    this.onSessionExpired = callback;
  }

  /**
   * 로그아웃 상태 설정
   */
  static setLoggingOut(state: boolean): void {
    this.isLoggingOut = state;
  }

  /**
   * 로그아웃 상태 확인
   */
  static isCurrentlyLoggingOut(): boolean {
    return this.isLoggingOut;
  }

  /**
   * 쿠키에서 Refresh Token 존재 여부 확인
   */
  static hasRefreshTokenCookie(): boolean {
    return document.cookie.includes('refresh_token=');
  }

  /**
   * Remember Me 상태는 더 이상 클라이언트에서 관리하지 않음
   * 쿠키의 Max-Age로 서버에서 관리됨
   */
  static getRememberMe(): boolean {
    // Refresh Token 쿠키가 있으면 로그인 상태로 간주
    return this.hasRefreshTokenCookie();
  }

  /**
   * 토큰 저장 (Access Token만 메모리에 저장, Refresh Token은 서버에서 쿠키로 설정)
   */
  static saveTokens(tokenResponse: TokenResponse): void {
    const now = new Date();
    const expiresAt = new Date(now.getTime() + (tokenResponse.expires_in * 1000));

    // Access Token을 메모리와 localStorage에 저장
    this.accessToken = tokenResponse.access_token;
    this.tokenExpiry = expiresAt;
    
    // localStorage에도 저장 (페이지 새로고침 대응)
    try {
      localStorage.setItem(this.ACCESS_TOKEN_KEY, tokenResponse.access_token);
      localStorage.setItem(this.TOKEN_EXPIRY_KEY, expiresAt.toISOString());
    } catch (error) {
      console.warn('localStorage 저장 실패:', error);
    }

    // 저장 후 즉시 검증
    const savedToken = this.getAccessToken();
    const savedExpiry = this.getTokenExpiry();
    const tokenInfo = this.getTokenInfo();

    console.log('🔑 Access Token saved to memory and localStorage', {
      expiresAt: expiresAt.toLocaleString(),
      expiresIn: `${tokenResponse.expires_in}초`,
      refreshTokenCookie: this.hasRefreshTokenCookie() ? '있음' : '없음',
      savedToken: savedToken ? '있음' : '없음',
      savedExpiry: savedExpiry ? savedExpiry.toLocaleString() : '없음',
      tokenInfo
    });

    // 저장 실패 시 에러 던지기
    if (!savedToken || !savedExpiry) {
      throw new Error('토큰 저장 실패');
    }
  }

  /**
   * Access Token 조회 (메모리에서, 없으면 localStorage에서)
   */
  static getAccessToken(): string | null {
    // 메모리에 있으면 반환
    if (this.accessToken) {
      return this.accessToken;
    }
    
    // 메모리에 없으면 localStorage에서 로드
    try {
      const storedToken = localStorage.getItem(this.ACCESS_TOKEN_KEY);
      if (storedToken) {
        this.accessToken = storedToken;
        console.log('🔄 Access Token loaded from localStorage');
        return storedToken;
      }
    } catch (error) {
      console.warn('localStorage 읽기 실패:', error);
    }
    
    return null;
  }

  /**
   * Refresh Token 조회 (쿠키에서)
   */
  static getRefreshToken(): string | null {
    // 쿠키에서 refresh_token 값 추출
    const cookies = document.cookie.split(';');
    for (let cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === 'refresh_token') {
        return value;
      }
    }
    return null;
  }

  /**
   * 토큰 만료 시간 조회 (메모리에서, 없으면 localStorage에서)
   */
  static getTokenExpiry(): Date | null {
    // 메모리에 있으면 반환
    if (this.tokenExpiry) {
      return this.tokenExpiry;
    }
    
    // 메모리에 없으면 localStorage에서 로드
    try {
      const storedExpiry = localStorage.getItem(this.TOKEN_EXPIRY_KEY);
      if (storedExpiry) {
        this.tokenExpiry = new Date(storedExpiry);
        console.log('🔄 Token expiry loaded from localStorage');
        return this.tokenExpiry;
      }
    } catch (error) {
      console.warn('localStorage 읽기 실패:', error);
    }
    
    return null;
  }

  /**
   * 토큰 정보 조회
   */
  static getTokenInfo(): TokenInfo {
    const accessToken = this.getAccessToken();
    const expiresAt = this.getTokenExpiry();

    if (!accessToken || !expiresAt) {
      return {
        isValid: false,
        isExpired: true,
        expiresAt: null,
        timeUntilExpiry: 0
      };
    }

    const now = new Date();
    const timeUntilExpiry = expiresAt.getTime() - now.getTime();
    const isExpired = timeUntilExpiry <= 0;

    return {
      isValid: !isExpired,
      isExpired,
      expiresAt,
      timeUntilExpiry: Math.max(0, timeUntilExpiry)
    };
  }

  /**
   * 토큰 갱신이 필요한지 체크 (만료 5분 전)
   */
  static needsRefresh(): boolean {
    const tokenInfo = this.getTokenInfo();

    if (!tokenInfo.isValid) {
      return true;
    }

    const bufferTime = this.REFRESH_BUFFER_MINUTES * 60 * 1000; // 5분을 밀리초로
    return tokenInfo.timeUntilExpiry <= bufferTime;
  }

  /**
   * 토큰 갱신
   */
  static async refreshTokens(): Promise<boolean> {
    // 로그아웃 진행 중이면 토큰 갱신하지 않음
    if (this.isLoggingOut) {
      console.log('🚫 Skipping token refresh - logout in progress');
      return false;
    }

    if (!this.hasRefreshTokenCookie()) {
      console.warn('⚠️ No refresh token cookie available');
      this.clearTokens();
      if (this.onSessionExpired) {
        this.onSessionExpired('no-token');
      }
      return false;
    }

    try {
      console.log('🔄 Refreshing access token...');

      const response = await fetch('/api/v1/auth/refresh', {
        method: 'POST',
        credentials: 'include', // 쿠키 포함하여 전송
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          console.warn('⚠️ Refresh token expired or invalid');
          this.clearTokens();
          if (this.onSessionExpired) {
            this.onSessionExpired('token-expired');
          }
          return false;
        }
        throw new Error(`Token refresh failed: ${response.status}`);
      }

      const tokenResponse: TokenResponse = await response.json();
      this.saveTokens(tokenResponse);

      console.log('✅ Tokens refreshed successfully');
      return true;

    } catch (error) {
      console.error('❌ Token refresh failed:', error);
      this.clearTokens();
      if (this.onSessionExpired) {
        this.onSessionExpired('refresh-failed');
      }
      return false;
    }
  }

  /**
   * 토큰 검증 및 필요시 갱신
   */
  static async ensureValidToken(): Promise<string | null> {
    // 로그아웃 진행 중이면 토큰 검증하지 않음
    if (this.isLoggingOut) {
      console.log('🚫 Skipping token validation - logout in progress');
      return null;
    }

    const tokenInfo = this.getTokenInfo();
    const accessToken = this.getAccessToken();
    const refreshToken = this.getRefreshToken();

    console.log('🔍 ensureValidToken check:', {
      tokenInfo,
      accessToken: accessToken ? '있음' : '없음',
      refreshToken: refreshToken ? '있음' : '없음',
      hasRefreshCookie: this.hasRefreshTokenCookie()
    });

    // 토큰이 없거나 만료된 경우
    if (!tokenInfo.isValid) {
      console.log('🔄 Token expired, attempting refresh...');
      const refreshed = await this.refreshTokens();
      const newToken = refreshed ? this.getAccessToken() : null;
      console.log('🔄 Token refresh result:', { refreshed, newToken: newToken ? '있음' : '없음' });
      return newToken;
    }

    // 갱신이 필요한 경우 (만료 5분 전)
    if (this.needsRefresh()) {
      console.log('🔄 Token expires soon, refreshing...');
      await this.refreshTokens(); // 실패해도 현재 토큰이 아직 유효하므로 계속 진행
    }

    const finalToken = this.getAccessToken();
    console.log('✅ Token validation successful:', { finalToken: finalToken ? '있음' : '없음' });
    return finalToken;
  }

  /**
   * 메모리와 localStorage에서 토큰 삭제
   */
  static clearTokens(): void {
    // 메모리에서 Access Token 삭제
    this.accessToken = null;
    this.tokenExpiry = null;
    
    // localStorage에서도 삭제
    try {
      localStorage.removeItem(this.ACCESS_TOKEN_KEY);
      localStorage.removeItem(this.TOKEN_EXPIRY_KEY);
    } catch (error) {
      console.warn('localStorage 삭제 실패:', error);
    }
    
    // 로그아웃 상태 초기화
    this.isLoggingOut = false;
    console.log('🗑️ Access tokens cleared from memory and localStorage');
  }

  /**
   * 로그인 상태 체크
   */
  static isLoggedIn(): boolean {
    const tokenInfo = this.getTokenInfo();
    return tokenInfo.isValid || this.hasRefreshTokenCookie();
  }

  /**
   * 토큰 만료 시간을 사람이 읽기 쉬운 형태로 반환
   */
  static getExpiryInfo(): {
    accessTokenExpiry: string;
    refreshTokenExpiry: string;
    timeUntilExpiry: string;
  } {
    const tokenInfo = this.getTokenInfo();
    const hasRefreshCookie = this.hasRefreshTokenCookie();

    let accessTokenExpiry = '토큰 없음';
    let timeUntilExpiry = '만료됨';

    if (tokenInfo.expiresAt) {
      accessTokenExpiry = tokenInfo.expiresAt.toLocaleString();

      if (tokenInfo.timeUntilExpiry > 0) {
        const minutes = Math.floor(tokenInfo.timeUntilExpiry / (1000 * 60));
        const seconds = Math.floor((tokenInfo.timeUntilExpiry % (1000 * 60)) / 1000);
        timeUntilExpiry = `${minutes}분 ${seconds}초`;
      }
    }

    // Refresh Token 만료 시간 계산 (정확한 시간은 서버에서만 알 수 있지만 대략적으로 계산)
    let refreshTokenExpiry = '토큰 없음';
    if (hasRefreshCookie && tokenInfo.expiresAt) {
      // Access token 발급 시점으로부터 30일 후로 추정
      const estimatedRefreshExpiry = new Date(tokenInfo.expiresAt.getTime() - (this.ACCESS_TOKEN_EXPIRE_MINUTES * 60 * 1000) + (this.REFRESH_TOKEN_EXPIRE_DAYS * 24 * 60 * 60 * 1000));
      refreshTokenExpiry = estimatedRefreshExpiry.toLocaleString();
    }

    return {
      accessTokenExpiry,
      refreshTokenExpiry,
      timeUntilExpiry
    };
  }
}

/**
 * 편의 함수들
 */
export const tokenUtils = {
  /**
   * Authorization 헤더 생성
   */
  getAuthHeader: (): { Authorization: string } | {} => {
    const token = TokenManager.getAccessToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  },

  /**
   * 토큰과 함께 fetch 요청
   */
  fetchWithAuth: async (url: string, options: RequestInit = {}): Promise<Response> => {
    const token = await TokenManager.ensureValidToken();

    if (!token) {
      throw new Error('No valid authentication token available');
    }

    const headers = {
      ...options.headers,
      'Authorization': `Bearer ${token}`
    };

    return fetch(url, {
      ...options,
      headers,
      credentials: 'include' // 쿠키 포함하여 전송
    });
  },

  /**
   * 토큰 상태 디버깅 (브라우저 콘솔에서 사용 가능)
   */
  debugTokenStatus: () => {
    const tokenInfo = TokenManager.getTokenInfo();
    const accessToken = TokenManager.getAccessToken();
    const refreshToken = TokenManager.getRefreshToken();
    const isLoggedIn = TokenManager.isLoggedIn();
    const hasRefreshCookie = TokenManager.hasRefreshTokenCookie();

    console.log('🔍 Token Debug Info:', {
      tokenInfo,
      accessToken: accessToken ? `${accessToken.substring(0, 20)}...` : '없음',
      refreshToken: refreshToken ? `${refreshToken.substring(0, 20)}...` : '없음',
      isLoggedIn,
      hasRefreshCookie,
      cookies: document.cookie
    });

    return {
      tokenInfo,
      accessToken: accessToken ? '있음' : '없음',
      refreshToken: refreshToken ? '있음' : '없음',
      isLoggedIn,
      hasRefreshCookie
    };
  }
};

// 브라우저 콘솔에서 사용할 수 있도록 전역 함수 추가
if (typeof window !== 'undefined') {
  (window as any).debugToken = tokenUtils.debugTokenStatus;
  (window as any).forceRefresh = async () => {
    console.log('🔄 Force refreshing tokens...');
    TokenManager.clearTokens();
    return await TokenManager.refreshTokens();
  };
  (window as any).checkCookies = () => {
    console.log('🍪 All cookies:', document.cookie);
    console.log('🍪 Refresh token cookie:', document.cookie.includes('refresh_token') ? '있음' : '없음');
    const refreshTokenMatch = document.cookie.match(/refresh_token=([^;]+)/);
    if (refreshTokenMatch) {
      console.log('🍪 Refresh token value:', refreshTokenMatch[1].substring(0, 20) + '...');
    }
  };
}